import React, { Suspense } from 'react';
import './styles.scss';
import { LoadOverlay } from '../../';
import { EScreenType } from '@naviair-utm/node-shared-interfaces';
import { ILoadOverlayProps } from '../../LoadOverlay';
import { LoaderSquare } from '../../LoaderSquare';

export interface IMainLayoutProps {
	/** Show overlay while content is loading externally */
	loadOverlayProps: ILoadOverlayProps;
	/** Which Navbar & style to render, dependant on {@link EScreenType }. Default {@link EScreenType.DESKTOP}*/
	screenState?: EScreenType;
	/** Navbar to render */
	navbar: React.ReactNode;
	/** Header to render. */
	header: React.ReactNode;
	children?: React.ReactNode;
}

/**
 * ## MainLayout
 *
 * Renders the main layout depending on screen state (such as mobile vs desktop).
 *
 * The main layout consists of a custom stylesheet, loading behavior and Navbar display.
 *
 * Pass in the correct navbar dependant on state.
 *
 * @example <MainLayout loadOverlayProps={loadOverlayProps} screenState={getScreenState} header={<Header />} navbar={screenState.mobile ? <NavMobile /> : <Nav />}>
 *	{props.children}
 * </MainLayout>
 *
 * @param props see {@link IMainLayoutProps}
 * @returns a full-screen div, handling LoadOverlay, header and Navbar dependant on screenState.
 */
export const MainLayout: React.FC<IMainLayoutProps> = (props) => {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	const screenState = props.screenState ?? EScreenType.DESKTOP; //set default value to be DESKTOP

	return (
		<div className={'layout'}>
			{props.loadOverlayProps.loading && <LoadOverlay {...props.loadOverlayProps} />}
			{props.header}
			{screenState === EScreenType.MOBILE ? (
				<>
					<div className={'body-mobile'}>
						<div className={'content-mobile'}>
							<Suspense fallback={<LoaderSquare />}>{props.children}</Suspense>
						</div>
					</div>
					{props.navbar} {/* mobile navbar */}
				</>
			) : (
				<div className={'body'}>
					{props.navbar} {/* regular desktop navbar */}
					<div className={'content'}>
						<Suspense fallback={<LoaderSquare />}>{props.children}</Suspense>
					</div>
				</div>
			)}
		</div>
	);
};
